import { useEffect, useState } from 'react';
import api from '../services/api';
import useAuth from './useAuth';

/**
 * 회원의 안 읽은 쪽지·고객센터 답변 수 — 5틀에 **같은 파일**을 둔다.
 *   unreadCount      : 쪽지 + 답변 합계
 *   noticeUnread     : 쪽지 화면에 있는 줄(쪽지 + 쪽지에 달린 답변) — 탭·상단 메뉴 숫자
 *   inquiryUnread    : 고객센터 화면에 있는 줄(문의에 달린 답변) — 탭·상단 메뉴 숫자
 *   gateNoticeUnread : 게임 전에 읽어야 하는 쪽지 — UnreadNoticeModal 판정(서버 게임 진입 확인과 같은 숫자)
 *   서버 GET /api/messages/unread-count 가 함께 반환(규칙은 user-api services/unreadMessageCounts.js).
 *
 * [2026-11-03] 부품마다 따로 돌던 조회를 탭당 하나로 합쳤다.
 *   WHY: 이 훅을 쓰는 부품(상단 메뉴·안내 창·탭 메뉴·현재 화면)마다 타이머·요청·숫자를 따로 들어, 디자인1 은 회원 한 명이 분당 18번까지 조회했고
 *        화면 쪽이 숫자를 다시 받아도 상단 메뉴 숫자는 자기 차례까지 옛 값이었다. 이제 숫자는 한 벌이고 모든 부품이 같은 값을 본다.
 *   갱신: 소켓 알림(새 쪽지·답변·읽음)이 주 경로이고 타이머는 알림을 놓쳤을 때(연결이 끊긴 사이 등)를 위한 것이다.
 */
const POLL_INTERVAL_MS = 60000;
const EMPTY_COUNTS = Object.freeze({ unreadCount: 0, noticeUnread: 0, inquiryUnread: 0, gateNoticeUnread: 0 });

const store = {
  counts: EMPTY_COUNTS,
  memberKey: null,
  inFlight: null,
  fetchAgain: false,
  timer: null,
  subscribers: new Set(),
};

const publish = (counts) => {
  store.counts = counts;
  store.subscribers.forEach((listener) => listener(counts));
};

/**
 * 숫자를 다시 받는다. 받는 중에 또 부르면 끝난 뒤 한 번 더 받는다 — 진행 중인 응답은 방금 일어난 읽음·도착을 반영하지 못했을 수 있다.
 * @returns {Promise<{unreadCount:number,noticeUnread:number,inquiryUnread:number,gateNoticeUnread:number}>} 최신 숫자(실패하면 직전 값)
 */
const fetchCounts = () => {
  if (!store.memberKey) return Promise.resolve(EMPTY_COUNTS);
  if (store.inFlight) {
    store.fetchAgain = true;
    return store.inFlight;
  }
  store.inFlight = (async () => {
    do {
      store.fetchAgain = false;
      const requestedFor = store.memberKey;
      try {
        // [2026-11-01] 주소를 매번 다르게 해 브라우저 캐시 줄서기를 피한다.
        //   WHY: 같은 주소 요청을 브라우저가 한 줄로 세워(동시 8건 2.7초, 쪽지 1건에 요청 10건) 먼저 확인한 옛 값(0)이 새 값 뒤에 도착했다.
        //        그 값이 게임 거절 안내 창을 닫아 회원에게 아무 안내가 남지 않았다(2026-11-01 ur2 실측 4회 중 4회).
        //        서버 응답에 저장 금지를 넣어도 브라우저는 응답을 보기 전에 줄을 세워 효과가 없었다(실측). 탭을 여러 개 열면 지금도 같은 일이 생긴다.
        const response = await api.get('/messages/unread-count', { params: { _: `${Date.now()}.${Math.random()}` } });
        // 응답이 오는 사이 다른 회원으로 바뀌었으면 버린다 — 앞 회원의 건수로 안내 창이 열리면 안 된다
        if (response.data.success && requestedFor === store.memberKey) {
          publish({
            unreadCount: response.data.data.unreadCount || 0,
            noticeUnread: response.data.data.noticeUnread || 0,
            inquiryUnread: response.data.data.inquiryUnread || 0,
            gateNoticeUnread: response.data.data.gateNoticeUnread || 0,
          });
        }
      } catch (error) {
        // [2026-11-01] 실패해도 직전 값을 유지한다 — 0 으로 바꾸면 안내 창이 닫히고, 다음 성공을 '늘어남'으로 잘못 본다(독립 검토)
        console.error('Failed to fetch unread count:', error);
      }
    } while (store.fetchAgain && store.memberKey);
    store.inFlight = null;
    return store.counts;
  })();
  return store.inFlight;
};

const onCountsMayHaveChanged = () => { fetchCounts(); };
const WATCHED_EVENTS = ['messageReply', 'messageRead', 'message:new'];

const startWatching = () => {
  WATCHED_EVENTS.forEach((name) => window.addEventListener(name, onCountsMayHaveChanged));
  store.timer = setInterval(fetchCounts, POLL_INTERVAL_MS);
};

const stopWatching = () => {
  WATCHED_EVENTS.forEach((name) => window.removeEventListener(name, onCountsMayHaveChanged));
  clearInterval(store.timer);
  store.timer = null;
};

const subscribe = (memberKey, listener) => {
  const memberChanged = store.memberKey !== memberKey;
  if (memberChanged) {
    // 지우지 않으면 같은 탭에서 다른 회원이 로그인할 때 앞 회원의 건수로 안내 창이 열린다(독립 검토)
    store.memberKey = memberKey;
    publish(EMPTY_COUNTS);
  }
  const isFirst = store.subscribers.size === 0;
  store.subscribers.add(listener);
  listener(store.counts);
  if (isFirst) startWatching();
  if (isFirst || memberChanged) fetchCounts();
  return () => {
    store.subscribers.delete(listener);
    if (store.subscribers.size === 0) {
      stopWatching();
      store.memberKey = null;
      store.counts = EMPTY_COUNTS;
    }
  };
};

const useUnreadCount = () => {
  const { user } = useAuth();
  const memberKey = user ? String(user.id ?? user.username ?? 'member') : null;
  const [counts, setCounts] = useState(store.counts);

  useEffect(() => {
    if (!memberKey) {
      setCounts(EMPTY_COUNTS);
      return undefined;
    }
    return subscribe(memberKey, setCounts);
  }, [memberKey]);

  return { ...counts, refetch: fetchCounts };
};

export default useUnreadCount;
